/**
 * What a shot would hit (docs/spec/02 §2, §6; 03 §2): shots are followed up the screen against the
 * forecast, the ones already flying first (their victims are as good as dead, so the next shot is
 * judged without them), then the volley the bot is thinking of firing.
 *
 * A captive is never to be shot. A shot is vetoed if it would strike one, or strike the Monarch that
 * holds one while it sits at home (killed there, its captive is lost), or pass a captive that only
 * something else stands in front of, or fly on into the air about a seated captive (it may take off
 * into a shot already on its way). The captive's own Monarch in flight is the one fair target: it
 * stops the shot, and killing it is the rescue.
 */

import * as math from '../sim/math';
import * as sim from '../sim';
import type { GameState } from '../sim';
import type { Forecast } from './forecast';
import * as horizon from './horizon';

// Local copies (see horizon.ts).
const { ENEMY_HIT, FIELD, FIGHTER, FORMATION, NOVA, SHOT } = sim;
const { HORIZON, MAX_ENEMIES, MUZZLE_Y, OFF, STRIDE } = horizon;

export const NOBODY = -1;
/** A victim that is not in `state.enemies`. */
export const EMPRESS = -2;

/** The engine's mark in a piercing shot's list for "has struck the Empress". */
const EMPRESS_ID = -1;
/** Below this a shot is still under the formation's lowest row at its widest. */
const FORMATION_FLOOR = 132;
/** Ticks before an enemy takes its seat from which it is treated as seated. */
const SEAT_MARGIN = 3;
/**
 * The air a seated captive could be in before a shot fired now has left the screen, should its
 * Monarch take off: this far either side of its column, and down to this far below the second row.
 */
const TAKE_OFF_HALF = 52;
const TAKE_OFF_BELOW = 8;

const SPREAD_SIN = math.sin(NOVA.spreadAngle);
const SPREAD_COS = math.cos(NOVA.spreadAngle);
const REACH_X = SHOT.halfW;
const REACH_Y = SHOT.halfH;

export class Gunnery {
  /** Hits on their way to each enemy, by index in `state.enemies`. */
  readonly pending = new Uint8Array(MAX_ENEMIES);
  /** Volleys in flight (the engine allows two, four with Rapid). */
  volleys = 0;

  /** After `strike`: who is hit first, the tick the shot is spent, and whether it must not be fired. */
  victim = NOBODY;
  end = 0;
  veto = false;

  /** After `volley`: shots of it that hit, whether one hits the mark, when the last is spent. */
  hits = 0;
  onMark = false;
  last = 0;
  vetoed = false;

  private captive = false;
  private shielded = false;
  private probing = false;
  private readonly ids: number[] = [];

  /** Follow every shot in flight to its end. */
  survey(s: GameState, f: Forecast): void {
    this.pending.fill(0, 0, Math.min(s.enemies.length, MAX_ENEMIES));
    this.ids.length = 0;
    for (const shot of s.shots) {
      if (!this.ids.includes(shot.volley)) this.ids.push(shot.volley);
      const struck = shot.struck.length > 0 ? shot.struck : null;
      this.strike(s, f, shot.x + shot.vx, shot.y + shot.vy, shot.vx, shot.vy, 1, shot.pierce, struck, 0);
      const v = this.victim;
      if (v >= 0 && v < MAX_ENEMIES) this.pending[v] = (this.pending[v] ?? 0) + 1;
    }
    this.volleys = this.ids.length;
  }

  /**
   * What a volley fired on tick `k` by a ship (or pair) centred on `x` would do; `mark` is the enemy
   * hoped for. The bot's eye may be out: by `wobble` pixels in where its own ship is, and by `slip`
   * ticks in where things in flight will be. The hits are what it believes; the veto is the truth.
   */
  volley(s: GameState, f: Forecast, x: number, wobble: number, k: number, mark: number, slip: number): void {
    const pw = s.powers;
    const speed = FIGHTER.shotSpeed * (pw.rapid > 0 ? NOVA.rapidShot : 1);
    this.hits = 0;
    this.onMark = false;
    this.last = 0;
    this.vetoed = false;
    if (s.player.dual) {
      this.one(s, f, x - FIGHTER.dualOffset, 0, -speed, wobble, k, mark, slip);
      this.one(s, f, x + FIGHTER.dualOffset, 0, -speed, wobble, k, mark, slip);
    } else this.one(s, f, x, 0, -speed, wobble, k, mark, slip);
    if (pw.spread > 0) {
      this.one(s, f, x, -SPREAD_SIN * speed, -SPREAD_COS * speed, wobble, k, mark, slip);
      this.one(s, f, x, SPREAD_SIN * speed, -SPREAD_COS * speed, wobble, k, mark, slip);
    }
  }

  private one(
    s: GameState, f: Forecast, x: number, vx: number, vy: number,
    wobble: number, k: number, mark: number, slip: number,
  ): void {
    const pierce = s.powers.pierce > 0;
    // A shot is made at the muzzle and moves once in the tick it is fired.
    this.strike(s, f, x + vx, MUZZLE_Y + vy, vx, vy, k, pierce, null, 0);
    if (this.veto) this.vetoed = true;
    if (this.end > this.last) this.last = this.end;
    if (wobble !== 0 || slip !== 0) this.strike(s, f, x + wobble + vx, MUZZLE_Y + vy, vx, vy, k, pierce, null, slip);
    if (this.victim !== NOBODY) {
      this.hits++;
      if (this.victim === mark) this.onMark = true;
    }
  }

  /**
   * Follow one shot that is at (x, y) after tick `from`. `struck` lists what a piercing shot has
   * been through already; `slip` reads everything in flight that many ticks out of place.
   */
  strike(
    s: GameState, f: Forecast, x: number, y: number, vx: number, vy: number,
    from: number, pierce: boolean, struck: readonly number[] | null, slip: number,
  ): void {
    const t = f.tracks;
    const count = t.count;
    this.victim = NOBODY;
    this.veto = false;
    this.end = 0;
    this.captive = f.captive;
    this.shielded = false;
    this.probing = false;
    let k = from;
    for (; k <= HORIZON; k++) {
      const seen = slip === 0 ? k : Math.max(0, Math.min(HORIZON, k + slip));
      for (let r = 0; r < count; r++) {
        const ey = t.y[r * STRIDE + seen] ?? OFF;
        if (ey <= 0) continue;
        const half = t.half[r] ?? ENEMY_HIT.small;
        if (Math.abs(y - ey) > REACH_Y + half) continue;
        if (Math.abs(x - (t.x[r * STRIDE + seen] ?? 0)) > REACH_X + half) continue;
        // About to sit down counts as sitting: a tick's error here would cost a captive.
        const seated = seen >= (t.seated[r] ?? HORIZON) - SEAT_MARGIN;
        if (this.touch(s, t.who[r] ?? NOBODY, k, pierce, struck, seated)) return;
      }
      if (f.seated > 0 && y < FORMATION_FLOOR) {
        // Rows are further apart than a shot's reach either way: only the two it lies between matter.
        const col = f.column(k, x);
        const below = Math.min(4, Math.ceil((y - FORMATION.top) / (f.pitch[k] ?? FORMATION.cell)));
        for (let row = below; row >= below - 1 && row >= -1; row--) {
          const half = row === 0 ? ENEMY_HIT.monarch : ENEMY_HIT.small;
          const i = f.seat(row, col);
          if (i < 0 || Math.abs(y - f.slotY(k, row)) > REACH_Y + half) continue;
          if (Math.abs(x - f.slotX(k, col)) > REACH_X + half) continue;
          if (this.touch(s, i, k, pierce, struck, true)) return;
        }
      }
      if (f.captiveCol >= 0 && !this.probing && y <= f.slotY(k, 1) + TAKE_OFF_BELOW
        && Math.abs(x - f.slotX(k, f.captiveCol)) <= TAKE_OFF_HALF) {
        this.veto = true;
        return;
      }
      if (f.empress && !this.probing && this.onEmpress(f, x, y, k, struck)) {
        if (this.victim === NOBODY) this.victim = EMPRESS;
        if (!pierce) {
          this.end = k;
          return;
        }
      }
      x += vx;
      y += vy;
      if (y <= -8 || x <= -4 || x >= FIELD.w + 4) break;
    }
    // A plain shot that hit something was spent there, however far the line was followed after.
    if (this.end === 0) this.end = k + 1;
  }

  private onEmpress(f: Forecast, x: number, y: number, k: number, struck: readonly number[] | null): boolean {
    if (struck !== null && struck.includes(EMPRESS_ID)) return false;
    return Math.abs(x - (f.ex[k] ?? 0)) <= NOVA.empressHalfW + REACH_X
      && Math.abs(y - (f.ey[k] ?? 0)) <= NOVA.empressHalfH + REACH_Y;
  }

  /** The shot overlaps enemy `i` on tick `k`. True when there is no more to learn from this shot. */
  private touch(
    s: GameState, i: number, k: number, pierce: boolean, struck: readonly number[] | null, seated: boolean,
  ): boolean {
    const e = s.enemies[i];
    if (!e || (struck !== null && struck.includes(e.id))) return false;
    const due = this.pending[i] ?? 0;
    // An earlier shot gets there first: this one flies through where it was.
    if (due >= e.hp) return false;
    if (e.kind === 'captive') {
      if (this.shielded) return false;
      this.veto = true;
      return true;
    }
    const holds = e.kind === 'monarch' && e.partner >= 0;
    // Killed at home its captive is lost: there it is not to be touched, even by a shot passing on to it.
    if (holds && seated) {
      this.veto = true;
      return true;
    }
    if (this.probing) return false;
    if (this.victim === NOBODY) {
      this.victim = i;
      if (!pierce) this.end = k;
    }
    // Its captive is behind it: a plain shot stops here, a piercing one only if this kills.
    if (holds) this.shielded = !pierce || e.hp - due <= 1;
    if (pierce) return false;
    if (!this.captive || this.shielded) return true;
    // Were this one to leave before the shot lands, the shot would carry on: look up the line.
    this.probing = true;
    return false;
  }
}
